import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import { ComplianceTypes } from '@/constants';
import {
  answerQuiz,
  AnswerQuizPayload,
  bookmarkSession,
  claimPlayerSession,
  fetchListenerData,
  fetchPlayerConfig,
  fetchPlayerSessions,
  fetchSession,
  pausePlayerSession,
  PauseSessionParams,
  playPlayerSession,
  PlaySessionParams,
  PostSeekPositionParams,
  PostSetPositionParams,
  resumeLastPlayerSession,
  seekPosition,
  setPosition,
  startPlayerSession,
  submitEpisodeEvaluation,
  submitSessionFeedback,
  unbookmarkSession,
} from '@/requests';
import { ListenerDataProps, SessionSummary } from '@/types';
import { camelCaseKeys } from '@/utils';

import {
  AnswerQuizResponse,
  ConfigResponse,
  FindPlayerSessionByEpisodeIdPayload,
  FindPlayerSessionByEpisodeIdResponse,
  ListenerData,
  PlayerSessionTransitionResponse,
  ResumeLastSessionPayload,
  ResumeLastSessionResponse,
  StartSessionPayload,
  StartSessionResponse,
  SubmitSessionFeedbackPayload,
} from '../player-fsm/player-fsm.types';
import { postgrestApi } from '../postgrest-api';
import { RootState } from '../store';
import { PostgrestApiTags } from '../store.constants';
import { handleOnQueryStartedError, rtkQueryError } from '../store.utils';
import { playerSessionsSelectors } from './player-fsm-api.selectors';
import {
  transformPlayerTransitionResponse,
  transformSessionResponse,
  transformSessionResponseNullable,
  transformSessionsResponse,
} from './player-fsm-api.utils';

export const playerSessionsAdapter = createEntityAdapter<
  SessionSummary,
  string
>({
  selectId: entity => entity.episodeId,
});

export const playerFsmApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchPlayerConfig: builder.query<ConfigResponse, void>({
      query: fetchPlayerConfig,
      transformResponse: (response: any) => {
        return {
          CAN: response.cpd,
          USA: response.cpe,
          defaultPlayer:
            response.default_player === 'cpe'
              ? ComplianceTypes.CPE
              : ComplianceTypes.CPD,
          isSingleSessionForced: !!response.force_single_session,
          rules: response.rules,
          isEpisodeFeedbackMigrated: !!response.episode_feedback_migrated,
          isEpisodeEvaluationMigrated:
            !!response.nasba_episode_evaluation_migrated,
        };
      },
      providesTags: [PostgrestApiTags.PLAYER_CONFIG],
    }),
    fetchPlayerSessions: builder.query<
      EntityState<SessionSummary, string>,
      void
    >({
      query: fetchPlayerSessions,
      transformResponse: (response: any[]) => {
        const sessions = transformSessionsResponse(response);
        return playerSessionsAdapter.setAll(
          playerSessionsAdapter.getInitialState(),
          sessions,
        );
      },
      providesTags: [PostgrestApiTags.PLAYER_SESSIONS],
    }),
    findPlayerSessionByEpisodeId: builder.query<
      FindPlayerSessionByEpisodeIdResponse,
      FindPlayerSessionByEpisodeIdPayload
    >({
      query: fetchSession,
      transformResponse: transformSessionResponseNullable,
      providesTags: (result, error, { episodeId }) => [
        { type: PostgrestApiTags.PLAYER_SESSION_DETAILS, id: episodeId },
      ],
    }),
    fetchListenerData: builder.query<ListenerData, ListenerDataProps | void>({
      query: fetchListenerData,
      transformResponse: (response: unknown) => camelCaseKeys(response),
      providesTags: [PostgrestApiTags.LISTENER_DATA],
    }),
    resumeLastPlayerSession: builder.mutation<
      ResumeLastSessionResponse,
      ResumeLastSessionPayload
    >({
      query: resumeLastPlayerSession,
      transformResponse: transformSessionResponseNullable,
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;

          const episodeId = data?.state.episodeId;

          if (!episodeId) return;

          const savedSession =
            playerFsmApi.endpoints.fetchPlayerSessions.select(undefined)(
              getState(),
            )?.data?.entities[episodeId] as SessionSummary;

          if (savedSession && savedSession.sessionId !== data.sessionId) {
            dispatch(
              postgrestApi.util.invalidateTags([
                PostgrestApiTags.PLAYER_SESSIONS,
                {
                  type: PostgrestApiTags.PLAYER_SESSION_DETAILS,
                  id: episodeId,
                },
              ]),
            );
          }
        } catch (error) {
          handleOnQueryStartedError(error);
        }
      },
    }),
    startPlayerSession: builder.mutation<
      StartSessionResponse,
      StartSessionPayload
    >({
      query: startPlayerSession,
      extraOptions: {
        withLock: true,
      },
      transformResponse: transformSessionResponse,
      onQueryStarted: async (
        { episodeId },
        { dispatch, queryFulfilled, getState },
      ) => {
        try {
          const { data } = await queryFulfilled;
          const savedSession =
            playerFsmApi.endpoints.fetchPlayerSessions.select(undefined)(
              getState(),
            )?.data?.entities[episodeId] as SessionSummary;

          if (savedSession && savedSession.sessionId !== data.sessionId) {
            dispatch(
              postgrestApi.util.invalidateTags([
                PostgrestApiTags.PLAYER_SESSIONS,
                {
                  type: PostgrestApiTags.PLAYER_SESSION_DETAILS,
                  id: episodeId,
                },
              ]),
            );
          }
        } catch (error) {
          handleOnQueryStartedError(error);
        }
      },
    }),
    playPlayerSession: builder.mutation<
      PlayerSessionTransitionResponse,
      PlaySessionParams
    >({
      query: playPlayerSession,
      transformResponse: transformPlayerTransitionResponse,
    }),
    pausePlayerSession: builder.mutation<
      PlayerSessionTransitionResponse,
      PauseSessionParams
    >({
      query: pausePlayerSession,
      transformResponse: transformPlayerTransitionResponse,
    }),
    setPosition: builder.mutation<
      PlayerSessionTransitionResponse,
      PostSetPositionParams
    >({
      query: setPosition,
      transformResponse: transformPlayerTransitionResponse,
    }),
    seekPosition: builder.mutation<
      PlayerSessionTransitionResponse,
      PostSeekPositionParams
    >({
      query: seekPosition,
      transformResponse: transformPlayerTransitionResponse,
    }),
    answerQuiz: builder.mutation<AnswerQuizResponse, AnswerQuizPayload>({
      query: answerQuiz,
      transformResponse: transformPlayerTransitionResponse<AnswerQuizResponse>,
      extraOptions: {
        withLock: true,
      },
      invalidatesTags: response => [
        PostgrestApiTags.PLAYER_SESSIONS,
        PostgrestApiTags.EPISODE_QUIZZES,
        {
          type: PostgrestApiTags.PLAYER_SESSION_DETAILS,
          id: response?.state.newState.episodeId,
        },
      ],
    }),
    claimEpisode: builder.mutation<any, { episodeId: string }>({
      queryFn: async ({ episodeId }, redux, extraOptions, baseQuery) => {
        try {
          const {
            data: { sessionId },
          } = camelCaseKeys(
            await baseQuery(startPlayerSession({ episodeId })),
          ) as {
            data: StartSessionResponse;
          };
          return baseQuery(claimPlayerSession({ sessionId }));
        } catch (error) {
          return rtkQueryError(error);
        }
      },
      extraOptions: {
        withLock: true,
      },
      invalidatesTags: [
        PostgrestApiTags.PLAYER_SESSIONS,
        PostgrestApiTags.PLAN,
        PostgrestApiTags.USER_TAGS,
      ],
    }),
    bookmarkSession: builder.mutation<
      PlayerSessionTransitionResponse,
      { episodeId: string; newIsBookmarked: boolean }
    >({
      queryFn: async (
        { episodeId, newIsBookmarked },
        redux,
        extraOptions,
        baseQuery,
      ) => {
        try {
          let sessionId;

          const existingSession = playerSessionsSelectors.selectById(
            redux.getState() as RootState,
            episodeId,
          );

          if (existingSession) {
            sessionId = existingSession.sessionId;
          } else {
            const { data, error: startSessionError } = await baseQuery(
              startPlayerSession({ episodeId }),
            );
            if (startSessionError) return { error: startSessionError };
            sessionId = camelCaseKeys(data).sessionId;
          }

          const query = newIsBookmarked
            ? bookmarkSession({
                sessionId,
                bookmarkedAt: new Date().toISOString(),
              })
            : unbookmarkSession({ sessionId });

          const { data: bookmarkData, error: bookmarkError } =
            await baseQuery(query);
          if (bookmarkError) return { error: bookmarkError };

          return {
            data: transformPlayerTransitionResponse(bookmarkData),
          };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
      extraOptions: {
        withLock: true,
      },
      invalidatesTags: [PostgrestApiTags.PLAYER_SESSIONS],
    }),
    submitSessionFeedback: builder.mutation<
      boolean,
      SubmitSessionFeedbackPayload
    >({
      query: submitSessionFeedback,
    }),
    submitEpisodeEvaluation: builder.mutation<
      PlayerSessionTransitionResponse,
      Parameters<typeof submitEpisodeEvaluation>[0]
    >({
      query: submitEpisodeEvaluation,
      transformResponse: transformPlayerTransitionResponse,
    }),
  }),
});

export const {
  useFetchPlayerConfigQuery,
  useFetchPlayerSessionsQuery,
  useFetchListenerDataQuery,
  useFindPlayerSessionByEpisodeIdQuery,
  useLazyFindPlayerSessionByEpisodeIdQuery,
  useStartPlayerSessionMutation,
  usePlayPlayerSessionMutation,
  useAnswerQuizMutation,
  useClaimEpisodeMutation,
  useBookmarkSessionMutation,
  useSubmitSessionFeedbackMutation,
  useSubmitEpisodeEvaluationMutation,
} = playerFsmApi;
